import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowUpDown,
  Check,
  Download,
  ListChecks,
  Pencil,
  RefreshCw,
  Search,
  Shield,
  TrendingUp,
  X,
} from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import './AutomationScoreboard.css';

const API_BASE_URL = process.env.REACT_APP_PERMISSIONS_API || '';
const APP_KEY = 'automation-scoreboard';
const DOWNLOADS = [
  { name: 'hit_list.csv', label: 'Hit list' },
  { name: 'universe.csv', label: 'Remaining work by subproject' },
  { name: 'app_rates.csv', label: '$ per use by app' },
  { name: 'lag_total_monthly.csv', label: 'GP impact by month' },
];
const STATE_FILTERS = [
  { value: 'all', label: 'All states' },
  { value: 'not using', label: 'Not using the app' },
  { value: 'using', label: 'Using the app' },
];

const COLUMNS = [
  { key: 'projectId', label: 'Project', sort: (row) => row.projectId },
  { key: 'subproject', label: 'Subproject', sort: (row) => row.subproject },
  { key: 'apps', label: 'Apps', sort: (row) => row.apps.map((app) => app.label).join(', ') },
  { key: 'remainingHours', label: 'Remaining hours', numeric: true, sort: (row) => row.remainingHours },
  { key: 'etcUsd', label: 'Remaining cost', numeric: true, sort: (row) => row.etcUsd },
  { key: 'pctComplete', label: '% complete', numeric: true, sort: (row) => row.pctComplete },
  { key: 'using', label: 'Using the app?', sort: (row) => (row.firstUse ? (row.usingRecently ? 2 : 1) : 0) },
  { key: 'eacChangeHours', label: 'Forecast change since first use', numeric: true, sort: (row) => row.eacChangePct },
  { key: 'state', label: 'State', sort: (row) => row.state },
  { key: 'action', label: 'Action', sort: (row) => `${row.action.owner} ${row.action.action}`.trim() },
  { key: 'due', label: 'Due', sort: (row) => row.action.due },
  { key: 'status', label: 'Status', sort: (row) => row.action.status },
];

const formatMoney = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '–';
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}M`;
  if (abs >= 1e3) return `${sign}$${Math.round(abs / 1e3).toLocaleString()}K`;
  return `${sign}$${Math.round(abs).toLocaleString()}`;
};

const formatHours = (value) => (value === null || value === undefined ? '–' : Math.round(value).toLocaleString());

const formatPct = (value, digits = 0) => (
  value === null || value === undefined ? '–' : `${(value * 100).toFixed(digits)}%`
);

const formatMonth = (value) => {
  if (!value) return '';
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' });
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, { dateStyle: 'medium', timeZone: 'UTC' });
};

const compareValues = (a, b) => {
  const aMissing = a === null || a === undefined || a === '';
  const bMissing = b === null || b === undefined || b === '';
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

// The scoreboard is one self-contained HTML document (its own CSS, its data
// inlined, plotly from jsDelivr). It is written into a same-origin iframe
// rather than fetched by URL because /api/* needs the bearer token, and the
// iframe grows to the document's height so the page scrolls as one.
function ScoreboardFrame({ html }) {
  const frameRef = useRef(null);
  const [height, setHeight] = useState(900);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || !html) return undefined;

    const doc = frame.contentDocument;
    doc.open();
    doc.write(html);
    doc.close();

    let cancelled = false;
    const measure = () => {
      if (cancelled || !frame.contentDocument) return;
      // offsetHeight is the document's own height. scrollHeight would be the
      // larger of that and the iframe's viewport, which grows with every
      // resize and never settles.
      const root = frame.contentDocument.documentElement;
      const body = frame.contentDocument.body;
      const next = Math.ceil(Math.max(root ? root.offsetHeight : 0, body ? body.offsetHeight : 0));
      if (next > 0) setHeight((prev) => (Math.abs(prev - (next + 24)) > 2 ? next + 24 : prev));
    };

    measure();
    const timers = [250, 1000, 2500].map((ms) => setTimeout(measure, ms));
    let observer = null;
    if (typeof ResizeObserver !== 'undefined' && doc.body) {
      observer = new ResizeObserver(measure);
      observer.observe(doc.body);
    }
    window.addEventListener('resize', measure);

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      if (observer) observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [html]);

  return (
    <iframe
      ref={frameRef}
      title="Automation GP Scoreboard"
      className="scoreboard-frame"
      style={{ height }}
      scrolling="no"
    />
  );
}

function ActionEditor({ row, statuses, onCancel, onSave, saving, error }) {
  const [form, setForm] = useState({
    owner: row.action.owner || '',
    action: row.action.action || '',
    due: row.action.due || '',
    status: row.action.status || '',
    notes: row.action.notes || '',
  });

  const update = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));

  return (
    <tr className="scoreboard-editor-row">
      <td colSpan={COLUMNS.length + 1}>
        <form
          className="scoreboard-editor"
          onSubmit={(event) => {
            event.preventDefault();
            onSave(form);
          }}
        >
          <div className="scoreboard-editor-title">
            <strong>{row.projectId}</strong> · {row.subproject}
            {row.name ? <span className="scoreboard-muted"> — {row.name}</span> : null}
          </div>
          <div className="scoreboard-editor-grid">
            <label>
              Owner
              <input value={form.owner} onChange={update('owner')} maxLength={200} placeholder="Who drives this" />
            </label>
            <label>
              Due
              <input type="date" value={form.due} onChange={update('due')} />
            </label>
            <label>
              Status
              <select value={form.status} onChange={update('status')}>
                <option value="">—</option>
                {statuses.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </label>
            <label className="scoreboard-editor-wide">
              Action
              <textarea
                value={form.action}
                onChange={update('action')}
                maxLength={1000}
                rows={2}
                placeholder="What will happen, e.g. David to sit with Clark and show the HIG tools; Clark to reduce hours in the November re-forecast"
              />
            </label>
            <label className="scoreboard-editor-wide">
              Notes
              <textarea value={form.notes} onChange={update('notes')} maxLength={1500} rows={2} placeholder="Context, decisions, who said what" />
            </label>
          </div>
          {error ? <div className="scoreboard-error">{error}</div> : null}
          <div className="scoreboard-editor-actions">
            {row.action.updatedAt ? (
              <span className="scoreboard-muted">
                Last saved {formatDateTime(row.action.updatedAt)}{row.action.updatedBy ? ` by ${row.action.updatedBy}` : ''}
              </span>
            ) : <span />}
            <div>
              <button type="button" className="scoreboard-btn ghost" onClick={onCancel} disabled={saving}>
                <X size={14} /> Cancel
              </button>
              <button type="submit" className="scoreboard-btn primary" disabled={saving}>
                <Check size={14} /> {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      </td>
    </tr>
  );
}

function HitList({ getAuthHeaders }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [appFilter, setAppFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'etcUsd', direction: 'desc' });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/scoreboard/hit-list`, { headers });
      if (response.status === 404) {
        setData({ rows: [], apps: [], statuses: [], refreshedAt: null, unpublished: true });
        return;
      }
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || `Failed to load the hit list (${response.status})`);
      }
      setData(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    const filtered = data.rows.filter((row) => {
      if (appFilter !== 'all' && !row.apps.some((app) => app.key === appFilter)) return false;
      if (stateFilter === 'not using' && row.firstUse) return false;
      if (stateFilter === 'using' && !row.firstUse) return false;
      if (needle) {
        const haystack = [
          row.projectId, row.name, row.customer, row.subproject, row.state,
          row.action.owner, row.action.action, row.action.status,
          ...row.apps.map((app) => app.label),
        ].join(' ').toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });

    const column = COLUMNS.find((col) => col.key === sort.key) || COLUMNS[4];
    const sign = sort.direction === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => sign * compareValues(column.sort(a), column.sort(b)));
  }, [data, appFilter, stateFilter, search, sort]);

  const totals = useMemo(() => {
    const sum = (predicate) => rows.filter(predicate).reduce((acc, row) => acc + (row.etcUsd || 0), 0);
    return {
      count: rows.length,
      etc: sum(() => true),
      notUsing: sum((row) => !row.firstUse),
      withActions: rows.filter((row) => row.action.action).length,
    };
  }, [rows]);

  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key === key) return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      const column = COLUMNS.find((col) => col.key === key);
      return { key, direction: column?.numeric ? 'desc' : 'asc' };
    });
  };

  const saveAction = async (row, form) => {
    try {
      setSaving(true);
      setSaveError(null);
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/api/scoreboard/actions/${encodeURIComponent(row.projectId)}/${encodeURIComponent(row.subproject)}`,
        { method: 'PUT', headers, body: JSON.stringify(form) }
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || `Could not save (${response.status})`);
      setData((prev) => ({
        ...prev,
        rows: prev.rows.map((item) => (
          item.projectId === row.projectId && item.subproject === row.subproject
            ? { ...item, action: body.action }
            : item
        )),
      }));
      setEditing(null);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const download = async (name) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/scoreboard/files/${name}`, { headers });
      if (!response.ok) throw new Error(`${name} is not available (${response.status})`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading && !data) {
    return <div className="scoreboard-panel scoreboard-muted">Loading the hit list…</div>;
  }

  if (error && !data) {
    return (
      <div className="scoreboard-panel">
        <div className="scoreboard-error">{error}</div>
        <button type="button" className="scoreboard-btn" onClick={load}><RefreshCw size={14} /> Retry</button>
      </div>
    );
  }

  if (data?.unpublished) {
    return (
      <div className="scoreboard-panel scoreboard-muted">
        The hit list has not been published yet. It appears after the first scheduled refresh.
      </div>
    );
  }

  const renderUsing = (row) => {
    if (!row.firstUse) return <span className="scoreboard-lose">no</span>;
    return (
      <>
        {row.usingRecently ? 'yes' : 'lapsed'}
        <span className="scoreboard-muted"> (since {formatMonth(row.firstUse)})</span>
      </>
    );
  };

  const renderChange = (row) => {
    if (row.eacChangePct === null || row.eacChangeHours === null) return <span className="scoreboard-muted">–</span>;
    const cls = row.eacChangePct <= -0.05 ? 'scoreboard-win' : row.eacChangePct >= 0.05 ? 'scoreboard-lose' : 'scoreboard-muted';
    const sign = row.eacChangeHours > 0 ? '+' : '';
    return (
      <span className={cls}>
        {sign}{formatHours(row.eacChangeHours)} h ({row.eacChangePct > 0 ? '+' : ''}{formatPct(row.eacChangePct)})
      </span>
    );
  };

  return (
    <div className="scoreboard-panel">
      <div className="scoreboard-toolbar">
        <div className="scoreboard-filters">
          <label className="scoreboard-search">
            <Search size={14} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search project, customer, owner…"
              aria-label="Search the hit list"
            />
          </label>
          <select value={appFilter} onChange={(event) => setAppFilter(event.target.value)} aria-label="Filter by app">
            <option value="all">All apps</option>
            {data.apps.map((app) => (
              <option key={app.key} value={app.key}>{app.label}</option>
            ))}
          </select>
          <select value={stateFilter} onChange={(event) => setStateFilter(event.target.value)} aria-label="Filter by state">
            {STATE_FILTERS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div className="scoreboard-downloads">
          {DOWNLOADS.map((file) => (
            <button key={file.name} type="button" className="scoreboard-btn ghost" onClick={() => download(file.name)} title={file.name}>
              <Download size={14} /> {file.label}
            </button>
          ))}
        </div>
      </div>

      <div className="scoreboard-summary">
        <span><strong>{totals.count}</strong> project-subprojects</span>
        <span><strong>{formatMoney(totals.etc)}</strong> of remaining cost</span>
        <span><strong>{formatMoney(totals.notUsing)}</strong> on projects not using the app</span>
        <span><strong>{totals.withActions}</strong> with an action</span>
        {data.refreshedAt ? <span className="scoreboard-muted">Forecasts as of the refresh on {formatDateTime(data.refreshedAt)}</span> : null}
      </div>

      {error ? <div className="scoreboard-error">{error}</div> : null}

      <div className="scoreboard-table-wrap">
        <table className="scoreboard-table">
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <th
                  key={column.key}
                  className={`${column.numeric ? 'numeric' : ''} ${sort.key === column.key ? `sorted ${sort.direction}` : ''}`}
                  aria-sort={sort.key === column.key ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  <button type="button" onClick={() => toggleSort(column.key)}>
                    {column.label}
                    <ArrowUpDown size={12} />
                  </button>
                </th>
              ))}
              <th aria-label="Edit" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={COLUMNS.length + 1} className="scoreboard-muted">No rows match these filters.</td></tr>
            ) : null}
            {rows.map((row) => {
              const key = `${row.projectId}|${row.subproject}`;
              const isEditing = editing === key;
              return (
                <React.Fragment key={key}>
                  <tr className={isEditing ? 'editing' : ''}>
                    <td>
                      <strong>{row.projectId}</strong>
                      {row.name ? <div className="scoreboard-muted scoreboard-small">{row.name}</div> : null}
                      {row.customer ? <div className="scoreboard-muted scoreboard-small">{row.customer}</div> : null}
                    </td>
                    <td>{row.subproject}</td>
                    <td>{row.apps.map((app) => app.label).join(', ')}</td>
                    <td className="numeric">{formatHours(row.remainingHours)}</td>
                    <td className="numeric">{formatMoney(row.etcUsd)}</td>
                    <td className="numeric">{formatPct(row.pctComplete)}</td>
                    <td>{renderUsing(row)}</td>
                    <td className="numeric">{renderChange(row)}</td>
                    <td>{row.state}</td>
                    <td className="scoreboard-action-cell">
                      {row.action.action ? (
                        <>
                          {row.action.owner ? <strong>{row.action.owner}: </strong> : null}
                          {row.action.action}
                        </>
                      ) : <span className="scoreboard-muted">none yet</span>}
                    </td>
                    <td>{formatDate(row.action.due)}</td>
                    <td>{row.action.status ? <span className={`scoreboard-status status-${row.action.status.replace(/\s+/g, '-')}`}>{row.action.status}</span> : null}</td>
                    <td className="scoreboard-edit-cell">
                      <button
                        type="button"
                        className="scoreboard-icon-btn"
                        onClick={() => { setSaveError(null); setEditing(isEditing ? null : key); }}
                        aria-label={`Edit the action for ${row.projectId} ${row.subproject}`}
                      >
                        <Pencil size={14} />
                      </button>
                    </td>
                  </tr>
                  {isEditing ? (
                    <ActionEditor
                      row={row}
                      statuses={data.statuses}
                      saving={saving}
                      error={saveError}
                      onCancel={() => setEditing(null)}
                      onSave={(form) => saveAction(row, form)}
                    />
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AutomationScoreboard() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const { trackAppLaunch } = usePermissions();
  const [tab, setTab] = useState(() => (window.location.hash === '#hit-list' ? 'hit-list' : 'scoreboard'));
  const [access, setAccess] = useState(null);
  const [status, setStatus] = useState(null);
  const [pageHtml, setPageHtml] = useState(null);
  const [pageState, setPageState] = useState('loading');
  const [error, setError] = useState(null);

  const getAuthHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: '/automation-scoreboard' } });
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  useEffect(() => {
    if (!isAuthenticated) return;
    trackAppLaunch(APP_KEY);
  }, [isAuthenticated, trackAppLaunch]);

  useEffect(() => {
    window.location.hash = tab === 'hit-list' ? 'hit-list' : '';
  }, [tab]);

  const load = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setError(null);
      setPageState('loading');
      const headers = await getAuthHeaders();

      const accessResponse = await fetch(`${API_BASE_URL}/api/scoreboard/access`, { headers });
      if (!accessResponse.ok) {
        const body = await accessResponse.json().catch(() => ({}));
        throw new Error(body.error || `Could not check access (${accessResponse.status})`);
      }
      const accessBody = await accessResponse.json();
      setAccess(accessBody);
      if (!accessBody.allowed) {
        setPageState('denied');
        return;
      }

      const [statusResponse, pageResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/scoreboard/status`, { headers }),
        fetch(`${API_BASE_URL}/api/scoreboard/page`, { headers }),
      ]);

      if (statusResponse.ok) setStatus(await statusResponse.json());

      if (pageResponse.status === 404) {
        setPageHtml(null);
        setPageState('unpublished');
        return;
      }
      if (!pageResponse.ok) {
        const body = await pageResponse.json().catch(() => ({}));
        throw new Error(body.error || `Could not load the scoreboard (${pageResponse.status})`);
      }
      setPageHtml(await pageResponse.text());
      setPageState('ready');
    } catch (err) {
      setError(err.message);
      setPageState('error');
    }
  }, [isAuthenticated, getAuthHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  if (isLoading || !isAuthenticated) {
    return null;
  }

  const refreshedAt = status?.refreshedAt;

  return (
    <div className="scoreboard-page">
      <div className="scoreboard-shell">
        <header className="scoreboard-header">
          <button type="button" className="scoreboard-back" onClick={() => navigate('/')}>
            <ArrowLeft size={16} /> Platform
          </button>
          <div className="scoreboard-heading">
            <div className="scoreboard-title">
              <TrendingUp size={22} />
              <h1>Automation GP Scoreboard</h1>
            </div>
            <p className="scoreboard-muted">
              GP impact of the Platform automation apps on fixed-price subprojects, and the hit list of projects with remaining work the apps target.
              {refreshedAt ? ` Refreshed ${formatDateTime(refreshedAt)}.` : ''}
            </p>
          </div>
          <div className="scoreboard-header-actions">
            <button type="button" className="scoreboard-btn ghost" onClick={load} disabled={pageState === 'loading'}>
              <RefreshCw size={14} className={pageState === 'loading' ? 'spinning' : ''} /> Reload
            </button>
          </div>
        </header>

        {pageState === 'denied' ? (
          <div className="scoreboard-panel scoreboard-denied">
            <Shield size={40} />
            <h2>Access restricted</h2>
            <p>
              Signed in as <strong>{user?.email}</strong>.
              {access?.registered
                ? ' The Automation GP Scoreboard is granted per user. Ask the automation team (Rayce Morgante) or IT to add you in Manage App Access.'
                : ' The app is not registered in the Platform database yet (dbo.Applications has no automation-scoreboard row).'}
            </p>
          </div>
        ) : (
          <>
            <nav className="scoreboard-tabs" aria-label="Scoreboard sections">
              <button type="button" className={tab === 'scoreboard' ? 'active' : ''} onClick={() => setTab('scoreboard')}>
                <TrendingUp size={15} /> Scoreboard
              </button>
              <button type="button" className={tab === 'hit-list' ? 'active' : ''} onClick={() => setTab('hit-list')}>
                <ListChecks size={15} /> Hit list
              </button>
            </nav>

            {tab === 'scoreboard' ? (
              <>
                {pageState === 'loading' ? <div className="scoreboard-panel scoreboard-muted">Loading the scoreboard…</div> : null}
                {pageState === 'error' ? (
                  <div className="scoreboard-panel">
                    <div className="scoreboard-error">{error}</div>
                    <button type="button" className="scoreboard-btn" onClick={load}><RefreshCw size={14} /> Retry</button>
                  </div>
                ) : null}
                {pageState === 'unpublished' ? (
                  <div className="scoreboard-panel scoreboard-muted">
                    The scoreboard has not been published yet. It appears after the first scheduled refresh
                    (<code>uv run scoreboard refresh</code> then <code>uv run scoreboard publish</code> in the automation_team_scoreboard repo).
                  </div>
                ) : null}
                {pageState === 'ready' && pageHtml ? (
                  <div className="scoreboard-frame-card">
                    <ScoreboardFrame html={pageHtml} />
                  </div>
                ) : null}
              </>
            ) : (
              <HitList getAuthHeaders={getAuthHeaders} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AutomationScoreboard;
